import { useState } from "react";
import { ConfigProblem, ConfigProblemWithMessage } from "components/hostedScrapingValidators";
import { NewProjectConfig, ProjectConfig } from "providers/HostedScrapingProvider";
import { CollectorType, ListLiteralInputConfig, UploadCsvListConfig, UploadJsonListConfig, WebhookInputConfig } from "providers/HostedScrapingProvider/types";
import { UploadFile, DownloadFile } from 'components/hosted-scraping/UploadFileDownloadFile';
import { AdditionalOptionsTextBox } from "./AdditionalOptionsTextBox";
import { DuplicateEntriesModal, dedupeList } from "./DuplicateEntriesModal";
import { ErrorProblemsBox } from "./ErrorProblemsBox";
import { getInputBoxLabelsHS, projectTypeToInputTitleHS } from "sdecontent";
import { cx } from "utils";
import { hasProblem, problemMessage } from "./projectProblems";

// The sdecontent input titles are singular and inconsistent ('URL', 'Asin',
// 'Query', ...), so map them to a friendly plural label for the duplicate copy.
const duplicateLabel = (projectType: CollectorType): string => {
  switch (projectTypeToInputTitleHS(projectType)) {
    case 'URL': return 'URLs';
    case 'Asin': return 'ASINs';
    case 'Query': return 'queries';
    case 'Product id': return 'product IDs';
    case 'Category': return 'categories';
    default: return 'entries';
  }
};

const uploadErrorToProblems = (err: Error): ConfigProblemWithMessage[] => {
  return [{problem: ConfigProblem.InvalidUploadedFile, message: err.message }];
}

const listLiternalMaxLinesWarning = (currentLines: number, maxLines: number | undefined) => {
  if (maxLines === undefined) {
    return null;
  }
  const full = currentLines >= maxLines;
  const className = cx("text-sm",  full ? "text-red-400" : "text-gray-400 dark:text-neutral-400");

  return (<div className={className}>{currentLines} / {maxLines} lines. If you need longer input upload it as a file</div>);
}

const ListLiteral = ({content, title, placeholder, problems, setProblems, onChange, maxLines, duplicateLabel}: {
    content: string,
    title: string,
    placeholder: string,
    problems: ConfigProblemWithMessage[],
    setProblems: (problems: ConfigProblemWithMessage[]) => void,
    onChange: (newValue: string) => void,
    maxLines?: number,
    duplicateLabel: string }
  ) => {
  const [showDuplicates, setShowDuplicates] = useState(false);
  const hasProblem = (problem: ConfigProblem) => problems.some(p => p.problem === problem);
  const hasDuplicates = hasProblem(ConfigProblem.DuplicatesInListLiteral);

  const handleChange = (newValue: string) => {
    if (maxLines === undefined) {
      onChange(newValue);
      return;
    }
    const lines = newValue.split('\n');
    setProblems([]);
    onChange(lines.slice(0, maxLines).join('\n'));
  };

  const removeDuplicates = () => {
    handleChange(dedupeList(content));
    setShowDuplicates(false);
  };

  const currentLines = content ? content.split('\n').length : 0;

  const textAreaClassName = cx("w-full shadow placeholder-gray-200 dark:placeholder-neutral-200", 
    ((maxLines !== undefined && currentLines >= maxLines) || problems.length > 0)
    ? "border-error-600"
    : "border-gray-200 dark:border-neutral-200"
  );

  return (
  <div>
      <div>{title}</div>
      <textarea className={textAreaClassName} value={content} placeholder={placeholder} onChange={(ev) => handleChange(ev.target.value)}/>
      {listLiternalMaxLinesWarning(currentLines, maxLines)}
      {hasProblem(ConfigProblem.EmptyLiteralList) && <div className="text-red-500 dark:text-error-500">Empty list. Please add or upload data.</div>}
      {hasProblem(ConfigProblem.InvalidLiteralList) && <div className="text-red-500 dark:text-error-500">Invalid elements in the list</div>}
      {hasDuplicates && (
        <div className="text-red-500 dark:text-error-500">
          The list includes{' '}
          <button
            type="button"
            onClick={() => setShowDuplicates(true)}
            className="underline font-medium"
          >
            duplicate {duplicateLabel}
          </button>
          . Please remove all duplicates.
        </div>
      )}
      {showDuplicates && (
        <DuplicateEntriesModal
          label={duplicateLabel}
          list={content}
          onRemove={removeDuplicates}
          onClose={() => setShowDuplicates(false)}
        />
      )}
      <ErrorProblemsBox allProblems={problems} interestingProblems={[ConfigProblem.EmptyLiteralList, ConfigProblem.InvalidLiteralList, ConfigProblem.BackendCostCalculationError]}/>
  </div>);
}

const WebhookInput = ({url, title, placeholder, problems, onChange}: {
    url: string,
    title: string,
    placeholder: string,
    problems: ConfigProblemWithMessage[],
    onChange: (newUrl: string | undefined) => void }
  ) => {
  const hasProblem = (problem: ConfigProblem) => problems.some(p => p.problem === problem);

  return (
  <div>
      <div>{title}</div>
      <AdditionalOptionsTextBox value={url} placeholder={placeholder} callback={(value) => onChange(value)}/>
      {hasProblem(ConfigProblem.InvalidWebhookURL) && <div className="text-red-500 dark:text-error-500">Invalid webhook URL</div>}
      <ErrorProblemsBox allProblems={problems} interestingProblems={[ConfigProblem.InvalidWebhookURL, ConfigProblem.BackendCostCalculationError]}/>
  </div>);
}

type IInputSettingsProps = {
  proj: NewProjectConfig | ProjectConfig,
  problems: ConfigProblemWithMessage[],
  setProj: (newProject: NewProjectConfig | ProjectConfig) => void,
  fileUploaded: (project: NewProjectConfig | ProjectConfig) => void,
  setProblems: (problems: ConfigProblemWithMessage[]) => void,
  setUploadFileProblems: (problems: ConfigProblemWithMessage[]) => void,
};

export const InputSettings = ({
  proj,
  problems,
  setProj,
  fileUploaded,
  setProblems,
  setUploadFileProblems
}: IInputSettingsProps) => {
  const inputBoxLabels = getInputBoxLabelsHS(proj.config.type);

  const fileInput: UploadJsonListConfig | UploadCsvListConfig = proj.input as UploadJsonListConfig | UploadCsvListConfig;

  const handleFileUpload = (fileName: string, inputKey: string) => {

    const input = {...fileInput};
    input.fileName = fileName;
    input.inputKey = inputKey;

    const changedProject = {...proj, input}
    setProj(changedProject);
    setUploadFileProblems([]);
    fileUploaded(changedProject);
  }

  const handleUploadFileError = (err: Error) => {
    const problems = uploadErrorToProblems(err);
    setUploadFileProblems(problems);
  }

  const handleFileRemove = () => {
    setProj({...proj, input: { ...fileInput, inputKey: null} });
  }

  const changeListLiteral = (newValue: string) => {
    setProj({...proj, input: {...proj.input as ListLiteralInputConfig, list: newValue}});
  }

  const changeWebhookInputUrl = (newValue: string | undefined) => {
    setProj({...proj, input: {...proj.input as WebhookInputConfig, url: newValue ?? ''}});
  }

  return proj.input.type === 'list_literal'
    ? <ListLiteral
      title={inputBoxLabels.listLiteralInputTitle}
      placeholder={inputBoxLabels.listLiteralInputPlaceholder}
      content={(proj.input as ListLiteralInputConfig)?.list ?? ''}
      problems={problems}
      setProblems={setProblems}
      onChange={changeListLiteral}
      maxLines={500}
      duplicateLabel={duplicateLabel(proj.config.type)}
      />
    : proj.input.type  === 'webhook_input'
    ? <WebhookInput
      title="Webhook URL"
      placeholder="The URL where the input list will be fetched from"
      url={(proj.input as WebhookInputConfig)?.url ?? ''}
      problems={problems}
      onChange={changeWebhookInputUrl}
    />
    : proj.input.type  === 'upload_json_list' && !fileInput.inputKey
    ? <>
        <UploadFile inputType={'upload_json_list'} fileUploadDone={handleFileUpload} uploadError={handleUploadFileError} projectType={ proj.config.type } />
        {hasProblem(problems, ConfigProblem.MissingUploadedFile) && <div className="text-red-500 dark:text-error-500">Missing file</div>}
        {hasProblem(problems, ConfigProblem.InvalidUploadedFile) && <div className="text-red-500 dark:text-error-500">{`Invalid file: ${problemMessage(problems, ConfigProblem.InvalidUploadedFile)}`}</div>}
      </>
    : proj.input.type  === 'upload_csv_list' && !fileInput.inputKey
    ? <>
        <UploadFile inputType={'upload_csv_list'} fileUploadDone={handleFileUpload} uploadError={handleUploadFileError} projectType={ proj.config.type } />
        {hasProblem(problems, ConfigProblem.MissingUploadedFile) && <div className="text-red-500 dark:text-error-500">Missing file</div>}
        {hasProblem(problems, ConfigProblem.InvalidUploadedFile) && <div className="text-red-500 dark:text-error-500">{`Invalid file: ${problemMessage(problems, ConfigProblem.InvalidUploadedFile)}`}</div>}
      </>
    : proj.input.type  === 'upload_json_list' && fileInput.inputKey
    ? <DownloadFile inputKey={fileInput.inputKey} inputType={'upload_json_list'} fileName={fileInput.fileName} fileRemovedCallback={handleFileRemove} />
    : proj.input.type  === 'upload_csv_list' && fileInput.inputKey
    ? <DownloadFile inputKey={fileInput.inputKey} inputType={'upload_csv_list'} fileName={fileInput.fileName} fileRemovedCallback={handleFileRemove} />
    : null
}
